import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
 Check, ArrowRight, Star, Users, Wine, Music, Sun, Anchor, Sparkles,
 ChefHat, Briefcase, Heart, Flame, ChevronDown, Building, Soup, GlassWater,
 PartyPopper, ShipWheel, Wind, Calendar
} from "lucide-react";
import {
 BOAT_HIRE_RATES, FOOD_PACKAGES, BEVERAGE_PACKAGE, BYO_PACKAGES,
 MAGIC_PHOTOS, CANAPE_SELECTION, BAR_TAB
} from "../data/cruiseData";
import SEOHead from "../components/SEOHead";

// Vibrant floating-venue palette
const CREAM = "#FFF7EE";
const BLUSH = "#FFE9E2";
const CORAL = "#E63E5E";
const HOTPINK = "#FF4B8B";
const SUNSET = "#FF8856";
const CHAMPAGNE = "#FFC850";
const INK = "#0F1B3D";
const INK_SOFT = "rgba(15,27,61,0.72)";

const QUICK_TRUST = [
 { Icon: Users, label: "Up to 120 Guests", sub: "Two full decks", accent: CORAL },
 { Icon: ChefHat, label: "Catering Available", sub: "Canapés to buffet", accent: HOTPINK },
 { Icon: Music, label: "Sound System Included", sub: "Bluetooth · DJ-ready", accent: SUNSET },
 { Icon: Sun, label: "Sydney Harbour Views", sub: "Bridge · Opera House", accent: CHAMPAGNE }];

const INCLUDED = [
 { Icon: Anchor, label: "Private vessel hire", sub: "Whole boat, just for you" },
 { Icon: ShipWheel, label: "Captain & crew", sub: "20+ years on the harbour" },
 { Icon: Wind, label: "Full harbour cruise", sub: "Bridge · Opera House · CBD" },
 { Icon: Music, label: "Bluetooth sound system", sub: "Bring your playlist · DJ-ready" },
 { Icon: Building, label: "Indoor + open decks", sub: "Air-conditioned saloon + sky-deck" },
 { Icon: ChefHat, label: "Catering options", sub: "Canapés · buffet · BYO" },
 { Icon: Sparkles, label: "Event flexibility", sub: "Decorate · style · customise" }];

const FAQS = [
 { q: "Can we decorate the boat?", a: "Absolutely. Bring balloons, signage, table décor, fairy lights, themed setups. Anything safe and non-permanent is welcome. We can also coordinate a stylist for you on request." },
 { q: "Can we BYO drinks?", a: "Yes, Magic is one of Sydney's only major BYO charter boats. Pick our BYO Package and bring your own beer, wine and spirits with zero corkage. We provide ice, glassware, bar staff and chilled storage." },
 { q: "What's the minimum number of guests?", a: "There's no minimum guest count for private charter, you can book the whole boat for 10 or 110. For catered menus, minimum 20 guests applies. 18th, 21st birthdays are capped at 50 guests." },
 { q: "Can you cater for dietary requirements?", a: "Yes, vegetarian, gluten-free, halal, kosher, nut allergies and most other dietary requirements can be accommodated. Just flag them at the time of booking and our chefs will tailor the menu." },
 { q: "Can we customise the packages?", a: "Every charter is custom. Mix canapés with buffet, add a beverage package, layer entertainment, swap timings. Send us your brief and we'll build a quote that fits your night, not a template." },
 { q: "How does boarding work?", a: "Boarding is from King Street Wharf, Darling Harbour. We greet every guest, run safety briefings discreetly, and the boat departs to your scheduled time. Wharf fees are $55 per city wharf." },
 { q: "Is there a public holiday surcharge?", a: "Yes, a +20% surcharge applies to total bookings on public holidays, with a 4-hour minimum charter." },
 { q: "Do you provide security?", a: "Security is required for 18th, 21st birthday parties, $350 for 1 guard up to 4 hours. For all other private functions, our crew handles guest management." }];

export default function Packages() {
 const [activeTab, setActiveTab] = useState("canapes");
 const [openFaq, setOpenFaq] = useState(0);

 React.useEffect(() => { document.title = "Boat Hire & Menus · Sydney Harbour Cruise Packages | Magic Cruises"; }, []);
 React.useEffect(() => {
  const io = new IntersectionObserver((entries) => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add("in"); }), { threshold: 0.12 });
  document.querySelectorAll(".reveal-up").forEach(el => io.observe(el));
  return () => io.disconnect();
 }, []);

 const canapePackages = FOOD_PACKAGES.filter(f => f.id === "set-canape" || f.id.startsWith("canape"));
 const buffetPackages = FOOD_PACKAGES.filter(f => f.id === "mini-buffet" || ["crystal", "emerald", "diamond"].includes(f.id));

 const TABS = [
  { id: "canapes", label: "Canapés", Icon: Sparkles, accent: HOTPINK },
  { id: "buffet", label: "Buffet Packages", Icon: Soup, accent: SUNSET },
  { id: "beverage", label: "Beverage", Icon: GlassWater, accent: CORAL },
  { id: "byo", label: "BYO Options", Icon: Wine, accent: CHAMPAGNE }];

 const GALLERY = [
  { img: MAGIC_PHOTOS.birthdayHarbourMagic, label: "40th birthday", accent: CORAL, span: "lg:col-span-2 lg:row-span-2" },
  { img: MAGIC_PHOTOS.corporateChampagneNetwork, label: "Corporate drinks", accent: SUNSET, span: "" },
  { img: MAGIC_PHOTOS.magicChristmasLongTable, label: "Christmas long table", accent: "#D14545", span: "" },
  { img: MAGIC_PHOTOS.privateFunctionFairyLights, label: "Fairy-lit setup", accent: CHAMPAGNE, span: "" },
  { img: MAGIC_PHOTOS.magicSaloonNight, label: "Decorated saloon", accent: HOTPINK, span: "" },
  { img: MAGIC_PHOTOS.hensPinkParty, label: "Hens party", accent: HOTPINK, span: "lg:col-span-2" },
  { img: MAGIC_PHOTOS.harbourSunsetGroup, label: "Sunset on deck", accent: SUNSET, span: "" },
  { img: MAGIC_PHOTOS.magicDancefloorPacked, label: "Dance floor", accent: "#A24FE0", span: "lg:col-span-2" }];

 return (
  <main className="overflow-hidden" data-testid="packages-page" style={{ backgroundColor: CREAM, color: INK }}>
   <SEOHead
    title="Sydney Harbour Cruise Packages, Boat Hire Rates & Menus | Magic Cruises"
    description="Boat hire rates from $2,000, canapé menus from $30 pp, buffet packages from $50 pp, beverage packages from $15.50 pp/hr and full BYO options on MV Sydney Magic. Custom Sydney Harbour cruise packages for 40 to 120 guests."
    image="https://customer-assets.emergentagent.com/job_magic-harbour-party/artifacts/srsraacs_sydney%20cruises%20boats.png"
    canonical="/packages"
    keywords="Sydney Harbour cruise packages, boat hire rates Sydney, canapé menu Sydney cruise, buffet packages Sydney boat, beverage package Sydney cruise, BYO cruise package Sydney"
    includeLocalBusiness
    breadcrumbs={[
     { name: "Home", url: "/" },
     { name: "Packages & Menus", url: "/packages" },
    ]}
   />

   {/* ════════ 1 · COMPACT HERO ════════ */}
   <section className="relative pt-32 lg:pt-36 pb-16 lg:pb-20 overflow-hidden" data-testid="packages-hero">
    <div className="absolute inset-0">
     <img src={MAGIC_PHOTOS.harbourSunsetGroup} alt="Group celebrating on Magic Cruises with Sydney Harbour Bridge and Opera House at sunset" className="w-full h-full object-cover scale-105" />
     <div className="absolute inset-0" style={{ background: `linear-gradient(165deg, rgba(15,27,61,0.55) 0%, rgba(230,62,94,0.32) 50%, rgba(15,27,61,0.78) 100%)` }} />
    </div>
    <div className="relative max-w-7xl mx-auto px-5 lg:px-10 text-white">
     <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 backdrop-blur-md" style={{ backgroundColor: "rgba(255,255,255,0.14)", border: "1px solid rgba(255,255,255,0.28)" }}>
      <PartyPopper className="w-3.5 h-3.5" style={{ color: CHAMPAGNE }} />
      <span className="text-[11px] uppercase tracking-[0.3em] font-black">Sydney Harbour · Magic Cruises</span>
     </div>
     <h1 className="font-black tracking-tight leading-[0.95] max-w-4xl reveal-up" style={{ fontSize: "clamp(2rem, 5vw, 4.5rem)", letterSpacing: "-0.022em", textShadow: "0 3px 30px rgba(0,0,0,0.4)" }}>
      Packages, boat hire<br /><span className="italic font-light" style={{ background: `linear-gradient(120deg, ${CHAMPAGNE} 0%, ${HOTPINK} 100%)`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>& catering options.</span>
     </h1>
     <p className="text-white/85 text-base lg:text-lg leading-[1.65] max-w-2xl mt-6 reveal-up font-light">
      Private Sydney Harbour cruises for birthdays, work events, Christmas parties, hens nights and milestone celebrations onboard Magic.
     </p>

     <div className="mt-7 flex flex-wrap gap-3 reveal-up">
      <Link to="/contact" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full font-black text-sm tracking-wide text-white shadow-xl hover:scale-[1.03] transition-all" style={{ background: `linear-gradient(120deg, ${CORAL} 0%, ${HOTPINK} 100%)`, boxShadow: `0 16px 40px -10px ${HOTPINK}99` }}>
       Get Quote <ArrowRight className="w-4 h-4" />
      </Link>
      <Link to="/availability" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full font-bold text-sm tracking-wide text-white border-2 hover:bg-white/10 transition-all" style={{ borderColor: "rgba(255,255,255,0.7)" }}>
       <Calendar className="w-4 h-4" /> Check Availability
      </Link>
     </div>

     {/* Trust icons strip */}
     <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-3 reveal-up">
      {QUICK_TRUST.map((t, i) => (
       <div key={i} className="px-4 py-3.5 rounded-2xl backdrop-blur-xl border flex items-center gap-3" style={{ backgroundColor: "rgba(255,255,255,0.1)", borderColor: "rgba(255,255,255,0.22)" }}>
        <div className="shrink-0 w-9 h-9 rounded-xl inline-flex items-center justify-center shadow-md" style={{ backgroundColor: t.accent }}>
         <t.Icon className="w-4.5 h-4.5 text-white" strokeWidth={2.4} />
        </div>
        <div className="min-w-0">
         <div className="font-black text-sm text-white leading-tight">{t.label}</div>
         <div className="text-white/70 text-[10px] uppercase tracking-[0.2em] font-bold mt-0.5 truncate">{t.sub}</div>
        </div>
       </div>
      ))}
     </div>
    </div>
   </section>

   {/* ════════ 2 · BOAT HIRE PRICING, FIRST after hero ════════ */}
   <section id="charter" className="relative py-20 lg:py-28" style={{ backgroundColor: CREAM }}>
    <div className="relative max-w-7xl mx-auto px-5 lg:px-10">
     <div className="grid lg:grid-cols-12 gap-8 items-end mb-12 reveal-up">
      <div className="lg:col-span-7">
       <div className="text-[11px] uppercase tracking-[0.32em] font-black mb-3 flex items-center gap-3" style={{ color: CORAL }}>
        <span className="w-8 h-px" style={{ backgroundColor: CORAL }} /> Boat hire pricing
       </div>
       <h2 className="font-black tracking-tight leading-[0.95]" style={{ fontSize: "clamp(2rem, 5vw, 4rem)", letterSpacing: "-0.022em", color: INK }}>
        Your own floating<br /><span className="italic font-light" style={{ color: CORAL }}>venue for the night.</span>
       </h2>
      </div>
      <div className="lg:col-span-5">
       <p className="text-base lg:text-lg leading-[1.7] font-light" style={{ color: INK_SOFT }}>
        Up to 120 guests. Two full decks. Pick your duration and time of day. All rates are for the whole boat.
       </p>
      </div>
     </div>

     {/* 3 cards: 3hr · 4hr · Custom */}
     <div className="grid md:grid-cols-3 gap-5 reveal-up">
      {/* 3 Hour Charter */}
      <div className="relative rounded-3xl bg-white p-7 lg:p-8 overflow-hidden shadow-[0_20px_50px_-20px_rgba(15,27,61,0.18)] hover:-translate-y-1 transition-all">
       <div className="absolute top-0 left-0 right-0 h-1.5" style={{ backgroundColor: CORAL }} />
       <div className="absolute -top-16 -right-16 w-44 h-44 rounded-full blur-3xl opacity-25" style={{ background: CORAL }} />
       <div className="relative">
        <div className="text-[10px] uppercase tracking-[0.32em] font-black" style={{ color: CORAL }}>3 Hour Charter</div>
        <div className="font-black tracking-tight mt-3 leading-none" style={{ color: INK, fontSize: "clamp(2.5rem, 5vw, 3.5rem)" }}>$2,000</div>
        <div className="text-xs uppercase tracking-[0.18em] font-bold mt-2" style={{ color: INK_SOFT }}>Starting from · Feb to Oct</div>
        <ul className="mt-6 space-y-2 text-sm font-light" style={{ color: INK_SOFT }}>
         <li className="flex gap-2 items-start"><Check className="w-3.5 h-3.5 mt-1 shrink-0" style={{ color: CORAL }} />Whole boat hire · up to 120 guests</li>
         <li className="flex gap-2 items-start"><Check className="w-3.5 h-3.5 mt-1 shrink-0" style={{ color: CORAL }} />Captain & full crew</li>
         <li className="flex gap-2 items-start"><Check className="w-3.5 h-3.5 mt-1 shrink-0" style={{ color: CORAL }} />Nov to Jan rate · $2,900</li>
        </ul>
        <Link to="/contact" className="mt-7 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] font-black border-b pb-1" style={{ color: CORAL, borderColor: CORAL }}>
         Enquire & book <ArrowRight className="w-3.5 h-3.5" />
        </Link>
       </div>
      </div>

      {/* 4 Hour Charter, featured */}
      <div className="relative rounded-3xl bg-white p-7 lg:p-8 overflow-hidden shadow-[0_25px_60px_-20px_rgba(15,27,61,0.28)] hover:-translate-y-1 transition-all">
       <div className="absolute top-0 left-0 right-0 h-1.5" style={{ background: `linear-gradient(90deg, ${HOTPINK} 0%, ${CORAL} 100%)` }} />
       <div className="absolute top-4 right-4 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[9px] uppercase tracking-[0.28em] font-black text-white shadow-md" style={{ backgroundColor: HOTPINK }}>
        <Star className="w-2.5 h-2.5" fill="currentColor" strokeWidth={0} /> Most popular
       </div>
       <div className="absolute -top-16 -right-16 w-44 h-44 rounded-full blur-3xl opacity-30" style={{ background: HOTPINK }} />
       <div className="relative">
        <div className="text-[10px] uppercase tracking-[0.32em] font-black" style={{ color: HOTPINK }}>4 Hour Charter</div>
        <div className="font-black tracking-tight mt-3 leading-none" style={{ color: INK, fontSize: "clamp(2.5rem, 5vw, 3.5rem)" }}>$2,400</div>
        <div className="text-xs uppercase tracking-[0.18em] font-bold mt-2" style={{ color: INK_SOFT }}>Starting from · Feb to Oct</div>
        <ul className="mt-6 space-y-2 text-sm font-light" style={{ color: INK_SOFT }}>
         <li className="flex gap-2 items-start"><Check className="w-3.5 h-3.5 mt-1 shrink-0" style={{ color: HOTPINK }} />Whole boat hire · up to 120 guests</li>
         <li className="flex gap-2 items-start"><Check className="w-3.5 h-3.5 mt-1 shrink-0" style={{ color: HOTPINK }} />Captain & full crew</li>
         <li className="flex gap-2 items-start"><Check className="w-3.5 h-3.5 mt-1 shrink-0" style={{ color: HOTPINK }} />Nov to Jan rate · $3,500</li>
        </ul>
        <Link to="/contact" className="mt-7 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] font-black border-b pb-1" style={{ color: HOTPINK, borderColor: HOTPINK }}>
         Enquire & book <ArrowRight className="w-3.5 h-3.5" />
        </Link>
       </div>
      </div>

      {/* Custom Charter */}
      <div className="relative rounded-3xl p-7 lg:p-8 overflow-hidden hover:-translate-y-1 transition-all" style={{ background: `linear-gradient(160deg, ${INK} 0%, ${INK} 60%, #1a2952 100%)`, color: "white" }}>
       <div className="absolute top-0 left-0 right-0 h-1.5" style={{ background: `linear-gradient(90deg, ${CHAMPAGNE} 0%, ${SUNSET} 100%)` }} />
       <div className="absolute -top-16 -right-16 w-44 h-44 rounded-full blur-3xl opacity-25" style={{ background: CHAMPAGNE }} />
       <div className="relative">
        <div className="text-[10px] uppercase tracking-[0.32em] font-black" style={{ color: CHAMPAGNE }}>Custom Charter</div>
        <div className="font-black tracking-tight mt-3 leading-none text-white" style={{ fontSize: "clamp(2rem, 4vw, 2.75rem)" }}>Built for you</div>
        <div className="text-xs uppercase tracking-[0.18em] font-bold mt-2 text-white/70">Duration · menu · drinks · décor</div>
        <ul className="mt-6 space-y-2 text-sm font-light text-white/80">
         <li className="flex gap-2 items-start"><Check className="w-3.5 h-3.5 mt-1 shrink-0" style={{ color: CHAMPAGNE }} />$650/hr Jan to Oct · $850/hr Nov to Dec</li>
         <li className="flex gap-2 items-start"><Check className="w-3.5 h-3.5 mt-1 shrink-0" style={{ color: CHAMPAGNE }} />Pick start time, route & inclusions</li>
         <li className="flex gap-2 items-start"><Check className="w-3.5 h-3.5 mt-1 shrink-0" style={{ color: CHAMPAGNE }} />Multi-day, daytime, late-night options</li>
        </ul>
        <Link to="/contact" className="mt-7 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] font-black border-b pb-1" style={{ color: CHAMPAGNE, borderColor: CHAMPAGNE }}>
         Get custom quote <ArrowRight className="w-3.5 h-3.5" />
        </Link>
       </div>
      </div>
     </div>

     {/* Footnote, wharf, holidays, staff */}
     <div className="mt-7 grid sm:grid-cols-3 gap-3 reveal-up">
      <div className="rounded-2xl bg-white p-4 text-sm" style={{ color: INK_SOFT }}>
       <div className="text-[10px] uppercase tracking-[0.28em] font-black mb-1" style={{ color: CORAL }}>Wharf fees</div>
       <div className="font-black" style={{ color: INK }}>$55 per city wharf</div>
      </div>
      <div className="rounded-2xl bg-white p-4 text-sm" style={{ color: INK_SOFT }}>
       <div className="text-[10px] uppercase tracking-[0.28em] font-black mb-1" style={{ color: SUNSET }}>Bar / wait staff</div>
       <div className="font-black" style={{ color: INK }}>$250 per staff · 3 to 4 hrs</div>
      </div>
      <div className="rounded-2xl bg-white p-4 text-sm" style={{ color: INK_SOFT }}>
       <div className="text-[10px] uppercase tracking-[0.28em] font-black mb-1" style={{ color: HOTPINK }}>Public holidays</div>
       <div className="font-black" style={{ color: INK }}>+20% surcharge · min 4hrs</div>
      </div>
     </div>
    </div>
   </section>

   {/* ════════ 3 · CATERING & MENUS, TABS + ACCORDIONS ════════ */}
   <section id="menus" className="relative py-20 lg:py-28" style={{ backgroundColor: BLUSH }}>
    <div className="relative max-w-7xl mx-auto px-5 lg:px-10">
     <div className="max-w-3xl mb-10 lg:mb-14 reveal-up">
      <div className="text-[11px] uppercase tracking-[0.32em] font-black mb-3 flex items-center gap-3" style={{ color: HOTPINK }}>
       <span className="w-8 h-px" style={{ backgroundColor: HOTPINK }} /> Catering & menus
      </div>
      <h2 className="font-black tracking-tight leading-[0.95]" style={{ fontSize: "clamp(2rem, 5vw, 4rem)", letterSpacing: "-0.022em", color: INK }}>
       Food & drinks<br /><span className="italic font-light" style={{ color: HOTPINK }}>done your way.</span>
      </h2>
     </div>

     {/* TAB BAR */}
     <div className="flex flex-wrap gap-2 lg:gap-3 mb-8 lg:mb-10 reveal-up" role="tablist">
      {TABS.map((t) => {
       const active = activeTab === t.id;
       return (
        <button
         key={t.id}
         role="tab"
         aria-selected={active}
         onClick={() => setActiveTab(t.id)}
         data-testid={`menu-tab-${t.id}`}
         className={`inline-flex items-center gap-2 px-5 py-3 rounded-full font-black text-xs lg:text-sm uppercase tracking-[0.22em] transition-all ${active ? "text-white shadow-lg scale-[1.02]" : "bg-white/70 hover:bg-white"}`}
         style={active ? { background: `linear-gradient(120deg, ${t.accent} 0%, ${CORAL} 100%)`, boxShadow: `0 12px 30px -8px ${t.accent}77` } : { color: INK_SOFT }}
        >
         <t.Icon className="w-4 h-4" /> {t.label}
        </button>
       );
      })}
     </div>

     {/* TAB CONTENT */}
     <div className="reveal-up in">
      {activeTab === "canapes" && (
       <div className="space-y-4">
        {canapePackages.map((f, i) => (
         <AccordionRow
          key={f.id}
          accent={[CORAL, HOTPINK, SUNSET, CHAMPAGNE][i % 4]}
          title={f.name}
          subtitle={f.tagline}
          price={f.price}
          items={f.items}
         />
        ))}
        {/* Cold + Hot canapé selection accordions */}
        <AccordionRow
         accent="#3FA3FF"
         title="Cold canapé choices"
         subtitle={`${CANAPE_SELECTION.cold.length} fresh options`}
         price="Included"
         items={CANAPE_SELECTION.cold}
        />
        <AccordionRow
         accent={CORAL}
         title="Hot canapé choices"
         subtitle={`${CANAPE_SELECTION.hot.length} chef-made options`}
         price="Included"
         items={CANAPE_SELECTION.hot}
        />
       </div>
      )}

      {activeTab === "buffet" && (
       <div className="space-y-4">
        {buffetPackages.map((f, i) => (
         <AccordionRow
          key={f.id}
          accent={[SUNSET, CHAMPAGNE, CORAL, HOTPINK][i % 4]}
          title={f.name}
          subtitle={f.tagline}
          price={f.price}
          items={f.items}
         />
        ))}
       </div>
      )}

      {activeTab === "beverage" && (
       <div className="space-y-4">
        <AccordionRow
         accent={CORAL}
         title="Beverage package"
         subtitle="Flat per-person per-hour pricing · 1 bar staff required"
         price={BEVERAGE_PACKAGE.perPerson}
         items={[
          `Beers, ${BEVERAGE_PACKAGE.inclusions.Beers.join(" · ")}`,
          `White wine, ${BEVERAGE_PACKAGE.inclusions["White Wine"].join(" · ")}`,
          `Red wine, ${BEVERAGE_PACKAGE.inclusions["Red Wine"].join(" · ")}`,
          `Sparkling, ${BEVERAGE_PACKAGE.inclusions.Sparkling.join(" · ")}`,
          `Non-alcoholic, ${BEVERAGE_PACKAGE.inclusions["Non-Alcoholic"].join(" · ")}`,
          BEVERAGE_PACKAGE.note]}
        />
        <AccordionRow
         accent={SUNSET}
         title="Bar tab / cash bar"
         subtitle="Pay-as-you-go or pre-paid tab · min $500 spend"
         price="Per drink"
         items={[
          `Beers ${BAR_TAB.beers.price}, ${BAR_TAB.beers.items.join(" · ")}`,
          `White wine ${BAR_TAB.whiteWine.price}, ${BAR_TAB.whiteWine.items.join(" · ")}`,
          `Red wine ${BAR_TAB.redWine.price}, ${BAR_TAB.redWine.items.join(" · ")}`,
          `Sparkling ${BAR_TAB.sparkling.price}, ${BAR_TAB.sparkling.items.join(" · ")}`,
          `Non-alcoholic ${BAR_TAB.nonAlcoholic.price}, ${BAR_TAB.nonAlcoholic.items.join(" · ")}`,
          `Spirits ${BAR_TAB.spirits.price}, ${BAR_TAB.spirits.items.join(" · ")}`,
          BAR_TAB.barStaff]}
        />
       </div>
      )}

      {activeTab === "byo" && (
       <div className="space-y-4">
        {BYO_PACKAGES.map((b, i) => (
         <AccordionRow
          key={b.id}
          accent={i === 0 ? CHAMPAGNE : SUNSET}
          title={b.name}
          subtitle={`${b.duration} · up to ${b.maxGuests} guests · ${b.periods}`}
          price={`${b.priceLow}, ${b.priceHigh}`}
          items={[...b.inclusions, ...b.notes.map(n => `Note: ${n}`)]}
         />
        ))}
       </div>
      )}
     </div>
    </div>
   </section>

   {/* ════════ 4 · WHAT'S INCLUDED ════════ */}
   <section className="relative py-20 lg:py-28" style={{ backgroundColor: CREAM }}>
    <div className="relative max-w-7xl mx-auto px-5 lg:px-10">
     <div className="grid lg:grid-cols-12 gap-8 items-end mb-12 reveal-up">
      <div className="lg:col-span-7">
       <div className="text-[11px] uppercase tracking-[0.32em] font-black mb-3 flex items-center gap-3" style={{ color: SUNSET }}>
        <span className="w-8 h-px" style={{ backgroundColor: SUNSET }} /> What's included
       </div>
       <h2 className="font-black tracking-tight leading-[0.95]" style={{ fontSize: "clamp(2rem, 5vw, 4rem)", letterSpacing: "-0.022em", color: INK }}>
        Every charter<br /><span className="italic font-light" style={{ color: SUNSET }}>comes with.</span>
       </h2>
      </div>
      <div className="lg:col-span-5">
       <p className="text-base lg:text-lg leading-[1.7] font-light" style={{ color: INK_SOFT }}>
        Whatever package you pick, every Magic charter ships with the basics, boat, crew, music, harbour. You only decide what to layer on top.
       </p>
      </div>
     </div>

     <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5 reveal-up">
      {INCLUDED.map((it, i) => {
       const tones = [CORAL, HOTPINK, SUNSET, CHAMPAGNE, "#3FA3FF", "#A24FE0", CORAL];
       const accent = tones[i % tones.length];
       return (
        <div key={i} className="group relative rounded-3xl bg-white p-6 lg:p-7 hover:-translate-y-1 transition-all shadow-[0_15px_40px_-15px_rgba(15,27,61,0.18)]">
         <div className="w-12 h-12 rounded-2xl inline-flex items-center justify-center shadow-md mb-4 group-hover:scale-110 group-hover:rotate-[-4deg] transition-transform duration-500" style={{ backgroundColor: accent }}>
          <it.Icon className="w-6 h-6 text-white" strokeWidth={2.4} />
         </div>
         <div className="font-black text-lg tracking-tight" style={{ color: INK }}>{it.label}</div>
         <div className="text-[11px] uppercase tracking-[0.2em] font-bold mt-2" style={{ color: INK_SOFT }}>{it.sub}</div>
        </div>
       );
      })}
     </div>
    </div>
   </section>

   {/* ════════ 5 · EVENT GALLERY ════════ */}
   <section className="relative py-20 lg:py-28" style={{ backgroundColor: BLUSH }}>
    <div className="relative max-w-7xl mx-auto px-5 lg:px-10">
     <div className="grid lg:grid-cols-12 gap-8 items-end mb-12 reveal-up">
      <div className="lg:col-span-7">
       <div className="text-[11px] uppercase tracking-[0.32em] font-black mb-3 flex items-center gap-3" style={{ color: CORAL }}>
        <span className="w-8 h-px" style={{ backgroundColor: CORAL }} /> Real events
       </div>
       <h2 className="font-black tracking-tight leading-[0.95]" style={{ fontSize: "clamp(2rem, 5vw, 4rem)", letterSpacing: "-0.022em", color: INK }}>
        See yourself<br /><span className="italic font-light" style={{ color: CORAL }}>onboard.</span>
       </h2>
      </div>
      <div className="lg:col-span-5">
       <p className="text-base lg:text-lg leading-[1.7] font-light" style={{ color: INK_SOFT }}>
        Birthdays, corporate drinks, Christmas parties, decorated setups, every photo is a real Magic Cruises night, on the harbour, with real guests.
       </p>
      </div>
     </div>

     <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4 auto-rows-[180px] lg:auto-rows-[220px] reveal-up">
      {GALLERY.map((g, i) => (
       <div key={i} className={`group relative rounded-2xl lg:rounded-3xl overflow-hidden shadow-[0_20px_50px_-20px_rgba(15,27,61,0.25)] hover:-translate-y-1 transition-all duration-500 ${g.span}`}>
        <img src={g.img} alt={g.label} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-[1500ms]" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F1B3D]/85 via-[#0F1B3D]/20 to-transparent" />
        <div className="absolute top-3 left-3 px-3 py-1.5 rounded-full text-[9px] uppercase tracking-[0.28em] font-black text-white shadow-md" style={{ backgroundColor: g.accent }}>
         {g.label}
        </div>
       </div>
      ))}
     </div>
    </div>
   </section>

   {/* ════════ 6 · FAQ ════════ */}
   <section className="relative py-20 lg:py-28" style={{ backgroundColor: CREAM }}>
    <div className="relative max-w-5xl mx-auto px-5 lg:px-10">
     <div className="max-w-3xl mb-10 lg:mb-12 reveal-up">
      <div className="text-[11px] uppercase tracking-[0.32em] font-black mb-3 flex items-center gap-3" style={{ color: HOTPINK }}>
       <span className="w-8 h-px" style={{ backgroundColor: HOTPINK }} /> FAQ
      </div>
      <h2 className="font-black tracking-tight leading-[0.95]" style={{ fontSize: "clamp(2rem, 5vw, 4rem)", letterSpacing: "-0.022em", color: INK }}>
       Everything<br /><span className="italic font-light" style={{ color: HOTPINK }}>you're wondering.</span>
      </h2>
     </div>

     <div className="space-y-3 reveal-up">
      {FAQS.map((f, i) => {
       const open = openFaq === i;
       return (
        <div key={i} className={`rounded-2xl bg-white border transition-all overflow-hidden ${open ? "shadow-[0_20px_50px_-20px_rgba(230,62,94,0.3)]" : "shadow-[0_8px_24px_-12px_rgba(15,27,61,0.12)]"}`} style={{ borderColor: open ? CORAL : "rgba(15,27,61,0.06)" }}>
         <button
          onClick={() => setOpenFaq(open ? -1 : i)}
          data-testid={`faq-${i}`}
          className="w-full px-6 py-5 flex items-center justify-between gap-4 text-left"
         >
          <div className="flex items-center gap-3 min-w-0">
           <span className="shrink-0 w-7 h-7 rounded-full inline-flex items-center justify-center font-black text-xs text-white" style={{ backgroundColor: open ? CORAL : INK }}>
            {String(i + 1).padStart(2, "0")}
           </span>
           <span className="font-black text-base lg:text-lg tracking-tight" style={{ color: INK }}>{f.q}</span>
          </div>
          <ChevronDown className={`w-5 h-5 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} style={{ color: open ? CORAL : INK_SOFT }} />
         </button>
         {open && (
          <div className="px-6 pb-6 pl-[3.875rem] text-sm lg:text-base leading-[1.7] font-light" style={{ color: INK_SOFT }}>
           {f.a}
          </div>
         )}
        </div>
       );
      })}
     </div>
    </div>
   </section>

   {/* ════════ 7 · FINAL CTA ════════ */}
   <section className="relative py-24 lg:py-32 overflow-hidden text-white">
    <div className="absolute inset-0">
     <img src={MAGIC_PHOTOS.harbourSunsetGroup} alt="" className="w-full h-full object-cover scale-110" />
     <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${INK}f2 0%, ${CORAL}cc 50%, ${HOTPINK}d9 100%)` }} />
    </div>
    <div className="relative max-w-3xl mx-auto px-5 lg:px-10 text-center">
     <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 border backdrop-blur-md" style={{ borderColor: "rgba(255,255,255,0.5)", backgroundColor: "rgba(255,255,255,0.12)" }}>
      <Flame className="w-3.5 h-3.5 animate-pulse" style={{ color: CHAMPAGNE }} fill="currentColor" strokeWidth={0} />
      <span className="text-[11px] uppercase tracking-[0.32em] font-black" style={{ color: CHAMPAGNE }}>Limited weekend dates</span>
     </div>
     <h2 className="font-black tracking-tight leading-[0.92] reveal-up" style={{ fontSize: "clamp(2.25rem, 5.5vw, 5rem)", letterSpacing: "-0.025em" }}>
      Ready to celebrate<br />
      <span className="italic font-light" style={{ background: `linear-gradient(120deg, ${CHAMPAGNE} 0%, #FFFFFF 50%, ${CHAMPAGNE} 100%)`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
       on Sydney Harbour?
      </span>
     </h2>
     <p className="text-white/85 text-base lg:text-lg leading-[1.65] mt-6 max-w-xl mx-auto font-light reveal-up">
      Get a custom quote in 24 hours or check live availability for your 2026 date.
     </p>
     <div className="mt-9 flex flex-wrap justify-center gap-3 lg:gap-4 reveal-up">
      <Link to="/contact" className="inline-flex items-center gap-2 px-7 py-4 rounded-full font-black text-sm tracking-wide text-white shadow-2xl hover:scale-[1.03] transition-all" style={{ background: `linear-gradient(120deg, ${CORAL} 0%, ${HOTPINK} 100%)`, boxShadow: `0 25px 60px -10px ${HOTPINK}99` }}>
       Get Quote <ArrowRight className="w-4 h-4" />
      </Link>
      <Link to="/availability" className="inline-flex items-center gap-2 px-7 py-4 rounded-full font-bold text-sm tracking-wide border-2 text-white hover:bg-white/10 transition-all" style={{ borderColor: "rgba(255,255,255,0.7)" }}>
       <Calendar className="w-4 h-4" /> Check Availability
      </Link>
     </div>
    </div>
   </section>

  </main>
 );
}

// ── Accordion row component for menus tab ──
function AccordionRow({ accent, title, subtitle, price, items }) {
 const [open, setOpen] = useState(false);
 return (
  <div className="rounded-2xl bg-white overflow-hidden border transition-all" style={{ borderColor: open ? accent : "rgba(15,27,61,0.06)", boxShadow: open ? `0 24px 60px -22px ${accent}66` : "0 10px 30px -15px rgba(15,27,61,0.15)" }}>
   <button onClick={() => setOpen(!open)} className="w-full px-5 lg:px-7 py-5 flex items-center justify-between gap-4 text-left">
    <div className="flex items-center gap-4 min-w-0">
     <div className="shrink-0 w-1 h-12 rounded-full" style={{ backgroundColor: accent }} />
     <div className="min-w-0">
      <div className="font-black text-base lg:text-xl tracking-tight" style={{ color: INK }}>{title}</div>
      <div className="text-[11px] uppercase tracking-[0.18em] font-bold mt-1 truncate" style={{ color: INK_SOFT }}>{subtitle}</div>
     </div>
    </div>
    <div className="flex items-center gap-3 shrink-0">
     <div className="font-black text-lg lg:text-xl tracking-tight whitespace-nowrap" style={{ color: accent }}>{price}</div>
     <ChevronDown className={`w-5 h-5 transition-transform ${open ? "rotate-180" : ""}`} style={{ color: open ? accent : INK_SOFT }} />
    </div>
   </button>
   {open && (
    <div className="px-5 lg:px-7 pb-6 pl-[2.5rem] lg:pl-[3.25rem]">
     <ul className="space-y-2.5">
      {items.map((it, k) => (
       <li key={k} className="flex gap-3 items-start text-sm font-light leading-relaxed" style={{ color: INK_SOFT }}>
        <Check className="w-3.5 h-3.5 mt-1 shrink-0" style={{ color: accent }} />
        <span>{it}</span>
       </li>
      ))}
     </ul>
    </div>
   )}
  </div>
 );
}
